/* Native, instance-scoped UI lifecycle only. Commerce and measurement are independent modules. */
(() => {
  "use strict";
  const instances = new Map();
  const sectionSelector = "[data-nimo-section-id]";
  const initialized = new WeakSet();

  // Shopify's native localization form is rendered on non-index templates
  // (cart, product, and checkout entry pages). The original landing-page
  // script owns its own selector, so keep this handler scoped to the native
  // `.nimo-localization` form and submit after either language or country is
  // selected. This also survives Theme Editor section reloads because the
  // listener is delegated from document and installed only once per script.
  function bindLocalizationForms() {
    if (window.__nimoLocalizationFormsBound) return;
    window.__nimoLocalizationFormsBound = true;
    document.addEventListener("change", (event) => {
      const select = event.target?.closest?.(
        ".nimo-localization select[name=\"language_code\"], .nimo-localization select[name=\"country_code\"]",
      );
      if (!select || select.disabled || !select.value) return;
      const form = select.form;
      if (!form || !form.matches(".nimo-localization")) return;
      if (typeof form.requestSubmit === "function") form.requestSubmit();
      else window.HTMLFormElement.prototype.submit.call(form);
    });
  }

  bindLocalizationForms();

  function syncReservationAnchors(excludedRoot) {
    const used = new Set();
    document
      .querySelectorAll(".nimo-reservation__grid[data-nimo-anchor]")
      .forEach((anchor) => {
        if (excludedRoot?.contains(anchor)) return;
        const base = anchor.dataset.nimoAnchor;
        const instance =
          anchor.closest(sectionSelector)?.dataset.nimoSectionId || "instance";
        anchor.id = used.has(base) ? `${base}-${instance}` : base;
        used.add(base);
      });
  }

  function mount(root) {
    if (initialized.has(root)) return;
    initialized.add(root);
    const controller = new AbortController();
    const { signal } = controller;
    const cleanups = [];
    instances.set(root, () => {
      controller.abort();
      cleanups.forEach((cleanup) => cleanup());
      initialized.delete(root);
    });

    // Repeated reservation sections keep independent forms and unique anchors.
    syncReservationAnchors();
    root.querySelectorAll("[data-nimo-address-country]").forEach((select) => {
      const value = select.dataset.default;
      if (
        value &&
        Array.from(select.options).some((option) => option.value === value)
      )
        select.value = value;
    });

    const menu = root.querySelector("[data-nimo-menu]");
    if (menu) {
      const media = window.matchMedia("(min-width: 900px)");
      const syncMenu = () => {
        menu.open = media.matches;
      };
      syncMenu();
      media.addEventListener("change", syncMenu);
      cleanups.push(() => media.removeEventListener("change", syncMenu));
      root.addEventListener(
        "keydown",
        (event) => {
          if (event.key === "Escape" && !media.matches && menu.open) {
            menu.open = false;
            menu.querySelector("summary")?.focus();
          }
        },
        { signal },
      );
      menu.addEventListener(
        "click",
        (event) => {
          if (event.target.closest("a") && !media.matches) menu.open = false;
        },
        { signal },
      );
    }

    root.querySelectorAll("[data-nimo-external-video]").forEach((button) => {
      button.addEventListener(
        "click",
        () => {
          const target = root.querySelector("[data-nimo-video-mount]");
          if (!target || target.childNodes.length) return;
          const id = button.dataset.videoId;
          const type = button.dataset.nimoExternalVideo;
          if (
            !id ||
            !/^[\w-]+$/.test(id) ||
            !["youtube", "vimeo"].includes(type)
          )
            return;
          const frame = document.createElement("iframe");
          frame.title = button.textContent.trim();
          frame.allow = "autoplay; fullscreen; picture-in-picture";
          frame.allowFullscreen = true;
          frame.referrerPolicy = "strict-origin-when-cross-origin";
          frame.src =
            type === "youtube"
              ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1`
              : `https://player.vimeo.com/video/${encodeURIComponent(id)}?autoplay=1`;
          target.append(frame);
          const previousHidden = button.hidden;
          button.hidden = true;
          cleanups.push(() => {
            frame.remove();
            // bfcache/editor remount reuses this DOM: restore the control whose
            // player was removed, rather than leaving an empty hidden player.
            button.hidden = previousHidden;
          });
        },
        { signal },
      );
    });
  }

  function mountWithin(root) {
    if (root.matches?.(sectionSelector)) mount(root);
    root.querySelectorAll(sectionSelector).forEach(mount);
  }

  function unmountWithin(root) {
    for (const [element, cleanup] of instances) {
      if (element === root || root.contains(element)) {
        cleanup();
        instances.delete(element);
      }
    }
    syncReservationAnchors(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => mountWithin(document), {
      once: true,
    });
  } else mountWithin(document);
  document.addEventListener("shopify:section:load", (event) =>
    mountWithin(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) =>
    unmountWithin(event.target),
  );
  window.addEventListener("pagehide", () => unmountWithin(document));
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) mountWithin(document);
  });
})();
